// components/Branch/BranchForm.jsx
import React, { useState, useEffect } from 'react';
import { BuildingOfficeIcon, CheckCircleIcon } from '@heroicons/react/24/solid';
import { motion } from 'framer-motion';
import { companyService } from '../../services/companyService';

const BranchForm = ({ onSubmit, onCancel, initialData, isEditing = false }) => {
  const [formData, setFormData] = useState(initialData || {
    name: '',
    companyId: ''
  });
  const [companies, setCompanies] = useState([]);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loadingCompanies, setLoadingCompanies] = useState(false);

  useEffect(() => {
    loadCompanies();
  }, []);

  const loadCompanies = async () => {
    try {
      setLoadingCompanies(true);
      const data = await companyService.getCompanies();
      setCompanies(data);
    } catch (err) {
      setError('Failed to load companies');
    } finally {
      setLoadingCompanies(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = 'Branch name is required';
    if (!formData.companyId) newErrors.companyId = 'Company is required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    setIsSubmitting(true);
    try {
      const cleanedData = {
        ...formData,
        companyId: parseInt(formData.companyId)
      };
      await onSubmit(cleanedData);
    } catch (error) {
      setErrors({ general: error.response?.data?.message || 'Error saving branch' });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="flex flex-col lg:flex-row gap-6 bg-gradient-to-br from-[var(--tint)] via-white to-[var(--tint)] p-6 rounded-2xl shadow-md">
      {/* Left Panel */}
      <div className="w-full lg:w-1/4 bg-gradient-to-br from-[var(--tint)] via-white to-[var(--tint)] rounded-2xl shadow-md p-6 border border-[var(--tint-line)] h-fit">
        <img
          src="/src/assets/branch/branchleft.png"
          alt="Branch Settings"
          className="w-48 h-48 object-contain mb-3"
        />
        <h2 className="text-lg font-semibold text-gray-800">Branch Settings</h2>
        <p className="text-gray-500 text-sm mt-1 leading-snug">
          Configure branch details and assign to companies.
        </p>
      </div>

      {/* Right Form Panel */}
      <motion.form
        onSubmit={handleSubmit}
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="flex-1 bg-gradient-to-br from-[var(--tint)] via-white to-[var(--tint)] rounded-xl shadow-sm p-6 border border-[var(--tint-line)]"
      >
        {errors.general && (
          <div className="p-4 bg-red-50 border border-red-200 rounded-lg mb-6">
            <p className="text-red-700 text-sm">{errors.general}</p>
          </div>
        )}

        {/* Basic Information */}
        <div className="grid grid-cols-1 gap-6 mb-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Branch Name *
            </label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              required
              placeholder="Enter branch name"
              className={`w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[var(--accent)] focus:border-[var(--accent)] outline-none shadow-sm text-gray-800 transition-colors ${
                errors.name ? 'border-red-500' : ''
              }`}
            />
            {errors.name && <p className="text-red-500 text-sm mt-1">{errors.name}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Company *
            </label>
            <select
              name="companyId"
              value={formData.companyId}
              onChange={handleChange}
              disabled={loadingCompanies || isEditing}
              className={`w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[var(--accent)] focus:border-[var(--accent)] outline-none shadow-sm bg-white ${
                errors.companyId ? 'border-red-500' : ''
              } ${loadingCompanies || isEditing ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              <option value="">Select Company</option>
              {companies.map(company => (
                <option key={company.id} value={company.id}>
                  {company.name}
                </option>
              ))}
            </select>
            {errors.companyId && <p className="text-red-500 text-sm mt-1">{errors.companyId}</p>}
            {(loadingCompanies || isEditing) && (
              <p className="text-gray-500 text-sm mt-1">
                {loadingCompanies ? 'Loading companies...' : 'Company cannot be changed for existing branches'}
              </p>
            )}
          </div>
        </div>

        {/* Form Actions */}
        <div className="flex justify-end gap-4 pt-6 border-t border-gray-200">
          <button
            type="button"
            onClick={onCancel}
            className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={isSubmitting}
            className="px-6 py-3 bg-[var(--accent)] text-white rounded-lg hover:bg-[var(--accent-strong)] disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium flex items-center gap-2"
          >
            {isSubmitting ? (
              <>
                <span className="animate-spin border-2 border-white border-t-transparent rounded-full w-4 h-4"></span>
                Saving...
              </>
            ) : (
              <>
                <CheckCircleIcon className="w-5 h-5" />
                {isEditing ? 'Update Branch' : 'Save Branch'}
              </>
            )}
          </button>
        </div>
      </motion.form>
    </div>
  );
};

export default BranchForm;